<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { ProjectSampleAssociationDeleteDocument } from '@/gql/graphql';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  sampleName: string;
  sampleId: number;
  projectId: number;
  projectName: string;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ProjectSampleAssociationDeleteDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.projectSampleAssociationDelete) {
    console.debug('Project-sample association deleted successfully');
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value =
      'Project-sample association deletion failed: ' + res.data;
    console.error('Project-sample association deletion failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error deleting project-sample association:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onDeletionConfirmed() {
  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({ projectId: props.projectId, sampleId: props.sampleId });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Storage Object Rename -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="trash" class="me-2" />
        Remove sample
        <strong>{{ props.sampleName }}</strong>
        from project
        <strong>{{ props.projectName }}</strong>
      </template>
      <!-- Content -->
      <div>
        <!-- Delete? Confirmation Message -->
        <p>
          Are you sure you want to remove the sample
          <strong> {{ props.sampleName }} </strong>
          from project
          <strong> {{ props.projectName }} </strong>?
        </p>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="mutationInProgress || mutationShowSuccess"
          @click="onDeletionConfirmed"
        >
          <span v-if="mutationInProgress">
            Removing Sample from Project
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Sample Removed from Project
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Remove Sample from Project </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
